import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type SsoRoleAssignmentRule = {
  id: number;
  attribute_value: string;
  domain_id: number;
  domain_name: string;
  role_id: number;
  role_name: string;
  created_at: string;
};

export type CreateSsoRoleAssignmentRulePayload = {
  attributeValue: string;
  domainId: number;
  roleId: number;
};

export type SsoRoleAssignmentRuleState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  rules: SsoRoleAssignmentRule[];
};

export const ssoRoleAssignmentRuleInitialState: SsoRoleAssignmentRuleState = {
  status: STATUS.IDLE,
  error: null,
  rules: [],
};

function isSsoRoleAssignmentRule(value: unknown): value is SsoRoleAssignmentRule {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.attribute_value === "string" &&
    typeof row.domain_id === "number" &&
    typeof row.domain_name === "string" &&
    typeof row.role_id === "number" &&
    typeof row.role_name === "string" &&
    typeof row.created_at === "string"
  );
}

function parseSsoRoleAssignmentRuleListResponse(data: unknown): SsoRoleAssignmentRule[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isSsoRoleAssignmentRule)) {
    return null;
  }
  return data;
}

export const fetchSsoRoleAssignmentRules = createAsyncThunk<
  SsoRoleAssignmentRule[],
  void,
  { rejectValue: string }
>("ssoRoleAssignmentRule/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/sso-configurations/role-assignment-rules", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    const rules = parseSsoRoleAssignmentRuleListResponse(data);
    if (!rules) {
      return rejectWithValue("Unexpected response shape");
    }
    return rules;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const createSsoRoleAssignmentRule = createAsyncThunk<
  SsoRoleAssignmentRule,
  CreateSsoRoleAssignmentRulePayload,
  { rejectValue: string }
>("ssoRoleAssignmentRule/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/sso-configurations/role-assignment-rules", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attribute_value: payload.attributeValue,
        domain_id: payload.domainId,
        role_id: payload.roleId,
      }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isSsoRoleAssignmentRule(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/**
 * Deletes a rule only. Domain access it already granted is deliberately left in place — revoking
 * that is a separate action on the domain's Access tab.
 */
export const deleteSsoRoleAssignmentRule = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("ssoRoleAssignmentRule/delete", async (ruleId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/sso-configurations/role-assignment-rules/${ruleId}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return ruleId;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const ssoRoleAssignmentRuleSlice = createSlice({
  name: "ssoRoleAssignmentRule",
  initialState: ssoRoleAssignmentRuleInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchSsoRoleAssignmentRules.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchSsoRoleAssignmentRules.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.rules = action.payload;
      })
      .addCase(fetchSsoRoleAssignmentRules.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(createSsoRoleAssignmentRule.fulfilled, (state, action) => {
        state.rules = [...state.rules, action.payload];
        state.error = null;
      })
      .addCase(deleteSsoRoleAssignmentRule.fulfilled, (state, action) => {
        state.rules = state.rules.filter((rule) => rule.id !== action.payload);
      });
  },
});

export const ssoRoleAssignmentRuleReducer = ssoRoleAssignmentRuleSlice.reducer;

export const selectSsoRoleAssignmentRules = (state: RootState) => state.ssoRoleAssignmentRule.rules;
export const selectSsoRoleAssignmentRuleStatus = (state: RootState) =>
  state.ssoRoleAssignmentRule.status;
export const selectSsoRoleAssignmentRuleError = (state: RootState) =>
  state.ssoRoleAssignmentRule.error;

/** Distinct values already in use, so the create form can offer them instead of re-typing. */
export const selectSsoRoleAssignmentAttributeValues = (state: RootState): string[] => [
  ...new Set(state.ssoRoleAssignmentRule.rules.map((rule) => rule.attribute_value)),
];
